// @ts-nocheck
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useApp, navigate } from '../store/AppStore';
import { ScreenShell, SvgIcon } from '../components/WebShell';
import { ExportModal, NewContactModal as NewContactModalComp, NewTicketModal } from '../components/Modals';
import ConfirmDialog from '../components/ConfirmDialog';
import SuccessDialog from '../components/SuccessDialog';
import CommonSearch from '../components/Common_Search/Common_Search_Component';
import MobileNumberInput, { COUNTRIES } from '../components/Mobile_Number/Mobile_Number';
import { useResponsive } from '../theme/responsive';
import { customerKey } from '../lib/customerLink';

// Split an E.164 phone ("+919840012345") into its country + national digits so
// the premium MobileNumberInput can render it.
function parsePhone(phone?: string | null) {
  const fallback = COUNTRIES.find((c) => c.code === 'IN')!;
  if (!phone) return { country: fallback, national: '' };
  const byLongest = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length);
  const match = byLongest.find((c) => phone.startsWith(c.dialCode));
  if (match) return { country: match, national: phone.slice(match.dialCode.length).replace(/\D/g, '') };
  return { country: fallback, national: phone.replace(/\D/g, '') };
}


function buildCustomers(tickets: any[]) {
  const map = new Map<string, any>();
  tickets.forEach(t => {
    const key = customerKey(t.customer);
    if (!map.has(key)) {
      // `requesterId` is the CRM contact id (ctc_…) when CRM is the source —
      // carry it so ticket-derived contacts can be edited/deleted in CRM too.
      map.set(key, { ...t.customer, _contactId: t.customer._contactId || t.requesterId || null, tags: t.customer.tags || [], tickets: [], openCount: 0, lastContact: null });
    }
    const c = map.get(key)!;
    c.tickets.push({ id: t.id, subject: t.subject, status: t.status, priority: t.priority, channel: t.channel, openedAgo: t.openedAgo });
    if (t.status === 'open' || t.status === 'pending') c.openCount += 1;
    if (!c.lastContact) c.lastContact = t.openedAgo;
  });
  return [...map.values()];
}

const SEGMENTS = [
  { id: 'all',         name: 'All contacts',         match: (_c: any) => true },
  { id: 'vip',         name: 'VIP',                  match: (c: any) => (c.tags || []).includes('vip') },
  { id: 'has-tickets', name: 'With open tickets',    match: (c: any) => c.openCount > 0 },
  { id: 'bounced',     name: 'Bounced contacts',     match: (c: any) => (c.tags || []).includes('bounce') || c.health === 'at-risk' },
  { id: 'dormant',     name: 'No tickets · dormant', match: (c: any) => c.tickets.length === 0 },
  { id: 'india',       name: 'India',                match: (c: any) => c.country === 'India' },
  { id: 'usa',         name: 'USA',                  match: (c: any) => c.country === 'USA' },
];

const HEALTH_COLOR: Record<string, string> = {
  good: 'var(--success)', attention: 'var(--warn)', 'at-risk': 'var(--danger)',
};

// Creation date — prefers the raw API timestamp, falls back to the "Mon Year"
// label, else an em-dash.
function createdLabel(c: any): string {
  if (c.createdAt) {
    const d = new Date(c.createdAt);
    if (!isNaN(d.getTime())) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return c.since || '—';
}

const PLUS_PATH = '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>';
const CAL_PATH = '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>';
const BLDG_PATH = '<rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01"/>';
const EDIT_PATH = '<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>';
const USER_PATH = '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>';
const MAIL_PATH = '<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>';
const TRASH_PATH = '<polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>';
const PHONE_PATH = '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>';

// Premium gradient avatars (consistent with the tickets screen).
const C_GRADS = [
  'linear-gradient(135deg,#0ea5e9,#6366f1)', 'linear-gradient(135deg,#f97316,#db2777)',
  'linear-gradient(135deg,#10b981,#0ea5e9)', 'linear-gradient(135deg,#a855f7,#ec4899)',
  'linear-gradient(135deg,#06b6d4,#3b82f6)', 'linear-gradient(135deg,#84cc16,#22c55e)',
  'linear-gradient(135deg,#f59e0b,#dc2626)',
];
function avatarBg(c: any): string {
  if (typeof c.color === 'string' && c.color.startsWith('linear')) return c.color;
  const seed = c.email || c.name || '';
  let h = 0; for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xffffffff;
  return C_GRADS[Math.abs(h) % C_GRADS.length];
}
const EXPORT_PATH = '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>';
const CHEVRON_D = '<polyline points="6 9 12 15 18 9"/>';

export function CustomersListScreen() {
  const { tickets, contacts, toast, addContact, updateContact, deleteContact, searchContacts, addTicket } = useApp();
  const { tableMode } = useResponsive();

  // Edit / delete contact flows (CRM-backed). `editTarget` holds the form draft;
  // `deleteTarget` the contact pending confirmation; `success` the done message.
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [editConfirm, setEditConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [rowBusy, setRowBusy] = useState(false);
  const [rowSuccess, setRowSuccess] = useState<string | null>(null);

  const submitEdit = async () => {
    if (!editTarget?._contactId) { setEditTarget(null); return; }
    setRowBusy(true);
    try {
      await updateContact(editTarget._contactId, {
        displayName: editTarget.name?.trim(),
        email: editTarget.email?.trim() || null,
        company: editTarget.org?.trim() || null,
      });
      setEditConfirm(false);
      setEditTarget(null);
      setRowSuccess('Contact updated in CRM.');
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to update contact.', 'danger');
    } finally { setRowBusy(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?._contactId) { setDeleteTarget(null); return; }
    setRowBusy(true);
    try {
      await deleteContact(deleteTarget._contactId);
      const name = deleteTarget.name;
      setDeleteTarget(null);
      setRowSuccess(`${name} was deleted from CRM.`);
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to delete contact.', 'danger');
    } finally { setRowBusy(false); }
  };

  // One row per customer: each contact takes the tickets grouped under it (by
  // contact id, else email), and ticket groups no contact claims follow.
  const customers = useMemo(() => {
    const fromTickets = buildCustomers(tickets);
    const claimed = new Set<any>();
    const fromContacts = (contacts as any[]).map(c => {
      const group = fromTickets.find(g => !claimed.has(g) && (
        (c._contactId && g._contactId === c._contactId) ||
        (c.email && (g.email || '').toLowerCase() === c.email.toLowerCase())));
      if (group) claimed.add(group);
      return {
        ...c,
        tickets: group ? group.tickets : [], openCount: group ? group.openCount : 0,
        lastContact: group ? group.lastContact : null, tags: c.tags || [],
      };
    });
    return [...fromContacts, ...fromTickets.filter(g => !claimed.has(g))];
  }, [tickets, contacts]);

  const [q, setQ] = useState('');

  // Server-side contact search. When `q` is set we query GET /v1/contacts?q=…
  // (debounced) and render those rows; empty query falls back to the local list.
  const [searchResults, setSearchResults] = useState<any[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimer = useRef<any>(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const term = q.trim();
    if (!term) { setSearchResults(null); setSearchLoading(false); return; }
    setSearchLoading(true);
    let cancelled = false;
    searchTimer.current = setTimeout(async () => {
      try {
        const rows = await searchContacts(term);
        if (!cancelled) setSearchResults(rows);
      } catch {
        if (!cancelled) { setSearchResults([]); toast('Search failed. Try again.', 'danger'); }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(searchTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const searching = q.trim().length > 0;
  const baseList = searching ? (searchResults ?? []) : customers;
  const [tagFilter, setTagFilter] = useState('all');
  const [segment, setSegment] = useState('all');
  const [showSegmentMenu, setShowSegmentMenu] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showNewContact, setShowNewContact] = useState(false);
  const [detailContact, setDetailContact] = useState<any>(null);
  // The contact a ticket is being raised for — opens New ticket with it picked.
  const [newTicketFor, setNewTicketFor] = useState<any>(null);

  const currentSegment = SEGMENTS.find(s => s.id === segment) ?? SEGMENTS[0];
  const allTags = useMemo(() => [...new Set(customers.flatMap((c: any) => c.tags || []))].slice(0, 8), [customers]);

  const filtered = useMemo(() => {
    // `q` is applied server-side; here we only apply segment + tag scoping.
    return baseList.filter((c: any) => {
      if (!currentSegment.match(c)) return false;
      if (tagFilter !== 'all' && !(c.tags || []).includes(tagFilter)) return false;
      return true;
    });
  }, [baseList, currentSegment, tagFilter]);

  const totalOpen = customers.reduce((s: number, c: any) => s + c.openCount, 0);

  return (
    <ScreenShell crumbs={[{ label: 'Contacts' }]}>
      {/* Page header */}
      <div className="sk-page-head">
        <div>
          <div className="sk-page-head-title">Contacts</div>
          <div className="sk-page-head-sub">
            {searching
              ? (searchLoading ? `Searching for "${q.trim()}"…` : `${filtered.length} result${filtered.length === 1 ? '' : 's'} for "${q.trim()}"`)
              : `${segment !== 'all' ? `Segment: ${currentSegment.name} · ` : ''}${filtered.length} of ${customers.length} contacts · ${totalOpen} open tickets`}
          </div>
        </div>
        <div className="sk-page-head-actions">
          <button
            className="t-btn t-btn--primary"
            onClick={() => setShowNewContact(true)}
            style={{
              fontSize: 14.5, fontWeight: 700, padding: '12px 24px', borderRadius: 12, gap: 9,
              border: 'none', color: '#fff',
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
              boxShadow: '0 12px 26px -8px rgba(79,70,229,0.55)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 16px 32px -8px rgba(79,70,229,0.6)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 12px 26px -8px rgba(79,70,229,0.55)'; }}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" dangerouslySetInnerHTML={{ __html: PLUS_PATH }} />
            New contact
          </button>
        </div>
      </div>

      {/* Search — above the list, like Inbox */}
      <div className="sk-search-row">
        <div className="sk-search-row-inner">
          <CommonSearch
            value={q}
            onChange={setQ}
            placeholder="Search name, email, phone, company…"
          />
        </div>
      </div>

      {/* Filter bar */}
      <div className="sk-filter-bar" style={{ gap: 6 }}>
        <span className="sk-filter-label">Tag:</span>

        <button className={`sk-ch-chip${tagFilter === 'all' ? ' is-active' : ''}`} onClick={() => setTagFilter('all')}>Any</button>
        {allTags.map(t => (
          <button key={t} className={`sk-ch-chip${tagFilter === t ? ' is-active' : ''}`} onClick={() => setTagFilter(t)}>{t}</button>
        ))}
      </div>

      {/* List — table on desktop, stacked cards on mobile/tablet */}
      {tableMode === 'cards' ? (
        <div className="sk-table-wrap">
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ink-3)' }}>
              {searchLoading ? 'Searching…' : searching ? `No contacts for "${q.trim()}"` : 'No contacts match these filters.'}
            </div>
          ) : (
            <div className="sk-card-list">
              {filtered.map((c: any, i: number) => (
                <div className="sk-data-card" key={c.email} onClick={() => setDetailContact(c)}>
                  <div className="sk-data-card-head">
                    <span className="t-dim" style={{ fontSize: 12, fontWeight: 600, marginRight: 2 }}>{i + 1}.</span>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', display: 'grid', placeItems: 'center', background: avatarBg(c), color: '#fff', fontSize: 12.5, fontWeight: 700, flexShrink: 0, border: '2px solid var(--card)', boxShadow: '0 0 0 1px rgba(16,24,40,0.06), 0 2px 5px rgba(16,24,40,0.10)' }}>{c.initials}</div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="sk-data-card-title">{c.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.email}</div>
                    </div>
                  </div>
                  <div className="sk-data-card-row"><span className="sk-data-card-label">Phone</span><span className="sk-data-card-value t-mono">{c.phone || '—'}</span></div>
                  <div className="sk-data-card-row"><span className="sk-data-card-label">Company</span><span className="sk-data-card-value">{c.org || '—'}</span></div>
                  <div className="sk-data-card-row"><span className="sk-data-card-label">Created</span><span className="sk-data-card-value">{createdLabel(c)}</span></div>
                  <div className="sk-data-card-row"><span className="sk-data-card-label">Tickets</span><span className="sk-data-card-value">{c.tickets.length}{c.openCount > 0 ? ` · ${c.openCount} open` : ''}</span></div>
                  <div className="sk-data-card-row"><span className="sk-data-card-label">Last contact</span><span className="sk-data-card-value">{c.lastContact || 'Never'}</span></div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
      <div className="sk-table-wrap">
        <div className="sk-table-card">
          <table className="t-table">
            <thead>
              <tr>
                <th style={{ width: 46 }}>S.No</th>
                <th style={{ width: 220 }}>Contact</th>
                <th style={{ width: 130 }}>Phone</th>
                <th style={{ width: 120 }}>Company</th>
                <th style={{ width: 84 }}>Tags</th>
                <th style={{ width: 112 }}>Created</th>
                <th style={{ width: 110 }}>Tickets</th>
                <th style={{ width: 96 }}>Last contact</th>
                <th style={{ width: 88, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr style={{ cursor: 'default' }}>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ink-3)' }}>
                    {searchLoading ? 'Searching…' : searching ? `No contacts for "${q.trim()}"` : 'No contacts match these filters.'}
                  </td>
                </tr>
              ) : filtered.map((c: any, i: number) => (
                <tr key={c.email} onClick={() => setDetailContact(c)}>
                  <td>
                    <span className="t-dim" style={{ fontSize: 12.5, fontWeight: 600 }}>{i + 1}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: '50%', display: 'grid', placeItems: 'center',
                        background: avatarBg(c), color: '#fff',
                        fontSize: 12.5, fontWeight: 700, flexShrink: 0, letterSpacing: '-0.3px',
                        border: '2px solid var(--card)',
                        boxShadow: '0 0 0 1px rgba(16,24,40,0.06), 0 2px 5px rgba(16,24,40,0.10)',
                      }}>{c.initials}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--ink)', letterSpacing: '-0.1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.name}
                          {c.health === 'at-risk' && (
                            <span title="At risk" style={{ marginLeft: 6, width: 7, height: 7, borderRadius: '50%', background: 'var(--danger)', display: 'inline-block', verticalAlign: 'middle' }} />
                          )}
                        </div>
                        <div className="t-mono" style={{ fontSize: 11.5, color: 'var(--ink-4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {c.phone ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ink-4)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.8 }} dangerouslySetInnerHTML={{ __html: PHONE_PATH }} />
                        <span className="t-mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>{c.phone}</span>
                      </span>
                    ) : <span className="t-dim" style={{ fontSize: 12, color: 'var(--ink-4)' }}>—</span>}
                  </td>
                  <td>
                    {c.org ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ink-4)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.8 }} dangerouslySetInnerHTML={{ __html: BLDG_PATH }} />
                        <span style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500 }}>{c.org}</span>
                      </span>
                    ) : <span className="t-dim" style={{ fontSize: 12, color: 'var(--ink-4)' }}>—</span>}
                    {c.role && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 19 }}>{c.role}</div>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {(c.tags || []).slice(0, 3).map((tag: string) => (
                        <span key={tag} style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent-ink)', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 999, padding: '2px 9px' }}>{tag}</span>
                      ))}
                      {(c.tags || []).length > 3 && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 999, padding: '2px 8px' }}>+{c.tags.length - 3}</span>
                      )}
                      {(c.tags || []).length === 0 && (
                        <span className="t-dim" style={{ fontSize: 12, color: 'var(--ink-4)' }}>—</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-3)' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ink-4)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.8 }} dangerouslySetInnerHTML={{ __html: CAL_PATH }} />
                      {createdLabel(c)}
                    </span>
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                      <span style={{
                        display: 'inline-grid', placeItems: 'center', minWidth: 26, height: 23, padding: '0 7px',
                        borderRadius: 999, fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
                        background: c.tickets.length > 0 ? 'var(--paper-2)' : 'transparent',
                        border: c.tickets.length > 0 ? '1px solid var(--line)' : '1px solid transparent',
                        color: c.tickets.length > 0 ? 'var(--ink)' : 'var(--ink-4)',
                      }}>
                        {c.tickets.length}
                      </span>
                      {c.openCount > 0 && (
                        <span className="t-chip t-chip--info" style={{ fontSize: 10.5 }}>
                          <span className="t-chip-dot" />{c.openCount}
                        </span>
                      )}
                    </span>
                  </td>
                  <td>
                    <span className="t-dim" style={{ fontSize: 12 }}>{c.lastContact || 'Never'}</span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {c._contactId ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                        <button
                          className="sk-row-act sk-row-act--edit"
                          title="Edit contact"
                          onClick={(e) => { e.stopPropagation(); setEditTarget({ ...c }); }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: EDIT_PATH }} />
                        </button>
                        <button
                          className="sk-row-act sk-row-act--danger"
                          title="Delete contact"
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: TRASH_PATH }} />
                        </button>
                      </div>
                    ) : (
                      <span className="t-dim" style={{ fontSize: 11, color: 'var(--ink-4)', display: 'block', textAlign: 'center' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {showSegmentMenu && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100 }} onClick={() => setShowSegmentMenu(false)} />
      )}

      {/* ── Premium contact detail card ──────────────────────────────── */}
      {detailContact && (
        <div
          onClick={() => setDetailContact(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(10,14,24,0.66)', backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)', display: 'grid', placeItems: 'center', padding: 24 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 560, maxWidth: '92vw', background: 'var(--card)', borderRadius: 18, overflow: 'hidden', border: '1px solid var(--accent-line)', boxShadow: '0 36px 90px -20px rgba(10,14,24,0.55)' }}
          >
            {/* accent strip */}
            <div style={{ height: 3, background: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 45%, #6366F1 100%)' }} />
            {/* header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 22px', borderBottom: '1px solid var(--accent-line)', backgroundImage: 'linear-gradient(135deg, var(--accent-soft) 0%, var(--card) 70%)' }}>
              <div style={{ width: 54, height: 54, borderRadius: '50%', display: 'grid', placeItems: 'center', background: avatarBg(detailContact), color: '#fff', fontSize: 18, fontWeight: 700, flexShrink: 0, border: '3px solid var(--card)', boxShadow: '0 8px 22px -6px rgba(79,70,229,0.4)' }}>{detailContact.initials}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--ink)', letterSpacing: '-0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{detailContact.name}</div>
                <div className="t-mono" style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{detailContact.email}</div>
              </div>
              <button onClick={() => setDetailContact(null)} title="Close" style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--ink-2)', cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: 15 }}>✕</button>
            </div>
            {/* detail grid */}
            <div style={{ padding: '20px 22px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
              {[
                { label: 'Phone',        icon: PHONE_PATH, value: detailContact.phone, mono: true },
                { label: 'Company',      icon: BLDG_PATH,  value: detailContact.org },
                { label: 'Created',      icon: CAL_PATH,   value: createdLabel(detailContact) },
                { label: 'Tickets',      icon: null,       value: `${detailContact.tickets.length}${detailContact.openCount ? ` · ${detailContact.openCount} open` : ''}` },
                { label: 'Last contact', icon: null,       value: detailContact.lastContact || 'Never' },
              ].map((f) => (
                <div key={f.label}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 6 }}>{f.label}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    {f.icon && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ink-4)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.85 }} dangerouslySetInnerHTML={{ __html: f.icon }} />}
                    <span style={{ fontSize: 13.5, color: f.value ? 'var(--ink)' : 'var(--ink-4)', fontFamily: f.mono ? 'var(--font-mono)' : undefined }}>{f.value || '—'}</span>
                  </div>
                </div>
              ))}
              {/* Tags — full width */}
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 6 }}>Tags</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(detailContact.tags || []).length > 0
                    ? detailContact.tags.map((tag: string) => (
                      <span key={tag} style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--accent-ink)', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 999, padding: '3px 11px' }}>{tag}</span>
                    ))
                    : <span style={{ fontSize: 13, color: 'var(--ink-4)' }}>No tags</span>}
                </div>
              </div>
            </div>
            {/* footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 22px', borderTop: '1px solid var(--line)', background: 'var(--paper-2)' }}>
              <button
                onClick={() => { if (detailContact.email) (window as any).location.href = 'mailto:' + detailContact.email; }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, padding: '9px 16px', borderRadius: 10, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--ink)', cursor: 'pointer' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: MAIL_PATH }} />
                Email
              </button>
              {detailContact._contactId && (
                <button
                  onClick={() => { setEditTarget({ ...detailContact }); setDetailContact(null); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, padding: '9px 16px', borderRadius: 10, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--ink)', cursor: 'pointer' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: EDIT_PATH }} />
                  Edit
                </button>
              )}
              <button
                onClick={() => { setNewTicketFor(detailContact); setDetailContact(null); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 700, padding: '9px 18px', borderRadius: 10, border: 'none', color: '#fff', background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)', boxShadow: '0 10px 24px -8px rgba(79,70,229,0.55)', cursor: 'pointer' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" dangerouslySetInnerHTML={{ __html: PLUS_PATH }} />
                New ticket
              </button>
            </div>
          </div>
        </div>
      )}

      <ExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={() => { toast('Exported contacts CSV.', 'success'); setShowExportModal(false); }}
      />
      <NewContactModalComp
        open={showNewContact}
        onCancel={() => setShowNewContact(false)}
        onCreate={(data: any) => { addContact(data); setShowNewContact(false); }}
      />
      <NewTicketModal
        open={newTicketFor !== null}
        initialCustomer={newTicketFor}
        onCancel={() => setNewTicketFor(null)}
        onCreate={(data: any) => { addTicket(data); setNewTicketFor(null); }}
      />

      {/* Edit contact — saved to CRM */}
      <ConfirmDialog
        visible={editTarget !== null}
        title="Edit contact"
        message="Changes are saved to SK-CRM. (Phone is managed in CRM and can't be changed here.)"
        confirmText="Save"
        cancelText="Cancel"
        confirmColor="var(--accent)"
        loading={rowBusy}
        onConfirm={() => setEditConfirm(true)}
        onCancel={() => { if (!rowBusy) setEditTarget(null); }}
        onClose={() => { if (!rowBusy) setEditTarget(null); }}
      >
        {editTarget && (
          <div style={{ marginTop: 8, textAlign: 'left' }}>
            {/* Contact identity header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 12, marginBottom: 18 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', display: 'grid', placeItems: 'center', background: avatarBg(editTarget), color: '#fff', fontSize: 14, fontWeight: 700, flexShrink: 0, border: '2px solid var(--card)', boxShadow: '0 0 0 1px rgba(16,24,40,0.06), 0 2px 5px rgba(16,24,40,0.10)' }}>{editTarget.initials}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--ink)', letterSpacing: '-0.2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{editTarget.name || 'Contact'}</div>
                <div className="t-mono" style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>{editTarget.email || '—'}</div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div className="sk-dlg-label"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: USER_PATH }} /><span>Name</span></div>
                <input className="sk-dlg-input" value={editTarget.name || ''} autoFocus
                  onChange={(e) => setEditTarget((t: any) => ({ ...t, name: e.target.value }))} placeholder="Full name" />
              </div>
              <div>
                <div className="sk-dlg-label"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: MAIL_PATH }} /><span>Email</span></div>
                <input className="sk-dlg-input" value={editTarget.email || ''}
                  onChange={(e) => setEditTarget((t: any) => ({ ...t, email: e.target.value }))} placeholder="name@company.com" />
              </div>
              <div>
                <div className="sk-dlg-label"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: BLDG_PATH }} /><span>Company</span></div>
                <input className="sk-dlg-input" value={editTarget.org || ''}
                  onChange={(e) => setEditTarget((t: any) => ({ ...t, org: e.target.value }))} placeholder="Company (optional)" />
              </div>

              {/* Mobile — premium component, read-only (CRM-managed). */}
              {(() => {
                const { country, national } = parsePhone(editTarget.phone);
                return (
                  <MobileNumberInput
                    label="MOBILE NUMBER (CRM-MANAGED)"
                    selectedCountry={country}
                    value={national}
                    editable={false}
                    onChangeText={() => {}}
                    onCountryChange={() => {}}
                    placeholder={national ? undefined : 'No number on file'}
                  />
                );
              })()}
            </div>
          </div>
        )}
      </ConfirmDialog>

      {/* Save changes — confirm before writing to CRM */}
      <ConfirmDialog
        visible={editConfirm}
        title="Save changes?"
        message={editTarget ? `Update ${editTarget.name || 'this contact'} in SK-CRM with the changes you made?` : ''}
        confirmText="Yes, save"
        cancelText="Go back"
        confirmColor="var(--accent)"
        loading={rowBusy}
        onConfirm={submitEdit}
        onCancel={() => { if (!rowBusy) setEditConfirm(false); }}
      />

      {/* Delete contact — confirm → CRM delete */}
      <ConfirmDialog
        visible={deleteTarget !== null}
        title="Delete contact?"
        message={deleteTarget ? `${deleteTarget.name} will be permanently removed from SK-CRM. This can't be undone.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        loading={rowBusy}
        onConfirm={confirmDelete}
        onCancel={() => { if (!rowBusy) setDeleteTarget(null); }}
      />

      <SuccessDialog
        visible={rowSuccess !== null}
        title="Done"
        message={rowSuccess ?? undefined}
        primaryAction={{ label: 'Done', onPress: () => setRowSuccess(null) }}
        onClose={() => setRowSuccess(null)}
      />
    </ScreenShell>
  );
}
